import * as THREE from 'three';
import orbVertex from './orbVertex.glsl?raw';
import orbFragment from './orbFragment.glsl?raw';
import { createOrbUniforms, syncOrbUniforms, type OrbUniforms } from './useOrbUniforms';
import { settings } from '@/config';

/**
 * The mystical amber energy orb material (see `settings.orb`). A custom `ShaderMaterial`:
 * a glass shell (Fresnel + HDR-env reflection/refraction) wrapping a raymarched volumetric
 * amber-energy interior (simplex → FBM → curl → domain-warp, with Beer-Lambert absorption).
 *
 * Applied to the `Orave_emble_S_3` sphere. `update()` advances the flow each frame; `setSphere`
 * keeps the world-space bounding sphere in sync (the emblem is animated), and `setEnvMap`
 * wires the sunset cubemap in once it finishes loading (procedural sky fallback until then).
 */
export class OrbMaterial extends THREE.ShaderMaterial {
  readonly orbUniforms: OrbUniforms;

  constructor() {
    const uniforms = createOrbUniforms();
    super({
      uniforms,
      vertexShader: orbVertex,
      fragmentShader: orbFragment,
      // Opaque: the glass + interior are composited in-shader (no alpha blending needed), so it
      // depth-writes cleanly and can be occluded by / occlude the scene like a solid object.
      transparent: false,
      // Front faces only — the raymarch starts at the front surface and marches inward.
      side: THREE.FrontSide,
      // Tone-mapped like the rest of the direct render, so its HDR emissive rolls off through
      // ACES and blooms nicely (the `<tonemapping_fragment>` include in the fragment).
      toneMapped: true,
      defines: { ORB_STEPS: Math.min(48, Math.max(8, Math.round(settings.orb.steps))) },
    });
    this.orbUniforms = uniforms;
  }

  /** Advance the animated flow (call every frame). */
  update(delta: number): void {
    this.orbUniforms.uTime.value += delta;
  }

  /** Keep the world-space bounding sphere current (the emblem mesh may be animated). */
  setSphere(center: THREE.Vector3, radius: number): void {
    this.orbUniforms.uSphereCenter.value.copy(center);
    this.orbUniforms.uSphereRadius.value = radius;
  }

  /** Bind the HDR environment cubemap for reflection/refraction (null → procedural sky). */
  setEnvMap(env: THREE.CubeTexture | null): void {
    this.orbUniforms.uEnvMap.value = env;
    const want = env ? '' : undefined;
    if (this.defines.ORB_ENVMAP !== want) {
      if (env) this.defines.ORB_ENVMAP = '';
      else delete this.defines.ORB_ENVMAP;
      this.needsUpdate = true; // recompile: the sampler is conditionally declared
    }
  }

  /** Re-read `settings.orb` (debug GUI); also refreshes the step count (recompile). */
  applySettings(): void {
    syncOrbUniforms(this.orbUniforms);
    const steps = Math.min(48, Math.max(8, Math.round(settings.orb.steps)));
    if (this.defines.ORB_STEPS !== steps) {
      this.defines.ORB_STEPS = steps;
      this.needsUpdate = true;
    }
  }
}
